import { isOfferedInvitation, type OfferedInvitation } from "odyssey-shared";
import { say } from "../words.ts";
import { post } from "./auth-request.ts";

/**
 * Changing the Party invitations setting (P2.13-T16, ADR-101 decision 6): the settings page posts the refresh token, the
 * character and the value to `character-party-invites`, which stores what it accepts and holds it for the character's next
 * invitation.
 */

/**
 * What a change came to: accepted, or why not. `refused` is the gateway's 400 for a value it does not offer and `rate` its
 * 429, each with the gateway's sentence; `lost` is a session that is gone (401), which the page leaves the world for;
 * `unanswered` is everything else: no answer, a gateway without the route or an answer that is not the gateway's.
 */
export type PartyInvitesOutcome = { readonly ok: true } | { readonly ok: false; readonly why: "refused" | "rate" | "unanswered"; readonly message: string } | { readonly ok: false; readonly why: "lost" };

/**
 * Changes a character's Party invitations setting.
 *
 * @param base The account origin.
 * @param refreshToken The refresh token this tab holds.
 * @param characterId The id of the character the tab plays.
 * @param value The setting to store.
 * @returns Accepted, or why not; it never rejects.
 */
export async function changePartyInvites(base: string, refreshToken: string, characterId: string, value: OfferedInvitation): Promise<PartyInvitesOutcome> {
  const answer = await post(base, "character-party-invites", { refreshToken, characterId, partyInvites: value });
  if (answer.ok) {
    const stands = typeof answer.body === "object" && answer.body !== null ? (answer.body as { characterId?: unknown; partyInvites?: unknown }) : {};
    return stands.characterId === characterId && isOfferedInvitation(stands.partyInvites) && stands.partyInvites === value ? { ok: true } : { ok: false, why: "unanswered", message: say("account.request.unreachable") };
  }
  if (answer.status === 401) {
    return { ok: false, why: "lost" };
  }
  const why = answer.status === 400 && answer.code === "partyInvites" ? "refused" : answer.status === 429 && answer.code === "rate" ? "rate" : "unanswered";
  return { ok: false, why, message: answer.message };
}
